import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { getDataSource } from "@/lib/data-source";
import { JOB_STATUSES, type JobStatus } from "@/lib/types";
import { StatusChip } from "@/components/StatusChip";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { StaffTodaySummary } from "@/components/staff/StaffTodaySummary";
import { listCuttingTiers } from "@/lib/products";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useFormatMoney } from "@/lib/currency";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

type ViewMode = "table" | "orders";
type OrdersSortKey = "order_number" | "product" | "quantity" | "price" | "status" | "placed";
type SortDir = "asc" | "desc";

export const Route = createFileRoute("/app/staff/jobs-table")({
  component: TablePage,
  validateSearch: (search: Record<string, unknown>) => ({
    filter: typeof search.filter === "string" ? search.filter : undefined,
  }),
});

function TablePage() {
  const nav = useNavigate();
  const { demoMode } = useDemoMode();
  const { profile, refresh, loading: authLoading } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const {
    data: jobs = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["staff-jobs", demoMode, showArchived],
    queryFn: () => ds.listJobs({ includeArchived: showArchived }),
  });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });
  const { data: staffProfiles = [] } = useQuery({
    queryKey: ["staff-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("role", "staff");
      if (error) throw error;
      return data ?? [];
    },
  });
  const search = Route.useSearch();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [myJobsOnly, setMyJobsOnly] = useState(false);
  const profileViewPref = (profile as unknown as { jobs_view_pref?: string } | null)
    ?.jobs_view_pref;
  const [view, setViewState] = useState<ViewMode | null>(
    profileViewPref === "orders" ? "orders" : profileViewPref === "table" ? "table" : null,
  );
  useEffect(() => {
    if (view !== null) return;
    if (authLoading) return;
    if (profileViewPref === "orders" || profileViewPref === "table") {
      setViewState(profileViewPref);
    } else {
      setViewState("table");
    }
  }, [profileViewPref, authLoading, view]);
  const prefsReady = view !== null;
  const [ordersSortKey, setOrdersSortKey] = useState<OrdersSortKey>("placed");
  const [ordersSortDir, setOrdersSortDir] = useState<SortDir>("desc");
  const toggleOrdersSort = (key: OrdersSortKey) => {
    if (ordersSortKey === key) {
      setOrdersSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrdersSortKey(key);
      setOrdersSortDir(key === "placed" ? "desc" : "asc");
    }
  };
  const setView = async (next: ViewMode) => {
    setViewState(next);
    if (!profile?.id || demoMode) return;
    const { error } = await supabase
      .from("profiles")
      .update({ jobs_view_pref: next })
      .eq("id", profile.id);
    if (error) {
      toast.error("Could not save view preference");
      return;
    }
    refresh();
  };
  const fmt = useFormatMoney();

  const handleStatusChange = async (jobId: string, newStatus: JobStatus) => {
    try {
      await ds.updateJobStatus(jobId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const twentyFourHoursAgo = new Date();
  twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

  const filtered = jobs.filter((j) => {
    const isArchived = !!(j as { archived_at?: string | null }).archived_at;
    if (showArchived ? !isArchived : isArchived) return false;
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    const brandMatch = brandFilter === "all" || j.customer?.brand === brandFilter;
    if (!brandMatch) return false;
    const myJobsMatch = !myJobsOnly || j.assignee_profile_id === profile?.id;
    if (!myJobsMatch) return false;
    if (
      q &&
      !`${j.job_title} ${j.customer?.company_name ?? ""}`.toLowerCase().includes(q.toLowerCase())
    )
      return false;

    // URL-based filters
    if (search.filter === "due-today") {
      if (!j.due_date) return false;
      const dueDate = new Date(j.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === today.getTime();
    }

    if (search.filter === "overdue") {
      if (!j.due_date || j.status === "Completed") return false;
      const dueDate = new Date(j.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() < today.getTime();
    }

    if (search.filter === "new-24h") {
      const createdDate = new Date(j.created_at);
      return createdDate.getTime() >= twentyFourHoursAgo.getTime();
    }

    if (search.filter === "print-queue") {
      return j.status === "Queued for Print" || j.status === "Printing";
    }

    return true;
  });

  const orderRows = useMemo(() => {
    // Orders view = jobs that came through customer checkout (have an order_number).
    // Drafts and staff-created jobs without an order_number are excluded.
    const placed = filtered.filter(
      (j) => (j as { order_number?: number | null }).order_number != null,
    );
    const rows = placed.map((j) => {
      const tier = cuttingTiers.find((t) => t.id === j.cutting_tier_id);
      const ratePerM = tier ? Number(tier.price_per_m) : 0;
      const metres = Number(j.metres ?? 0);
      const price = metres > 0 && ratePerM > 0 ? metres * ratePerM : null;
      return { job: j, price };
    });
    const dir = ordersSortDir === "asc" ? 1 : -1;
    const cmp = (a: number | string | null, b: number | string | null) => {
      const aNull = a === null || a === undefined;
      const bNull = b === null || b === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls always last
      if (bNull) return -1;
      if (typeof a === "number" && typeof b === "number") return (a - b) * dir;
      return String(a).localeCompare(String(b)) * dir;
    };
    const key = ordersSortKey;
    rows.sort((ra, rb) => {
      const a = ra.job;
      const b = rb.job;
      switch (key) {
        case "order_number": {
          const ao = (a as { order_number?: number | null }).order_number ?? null;
          const bo = (b as { order_number?: number | null }).order_number ?? null;
          return cmp(ao, bo);
        }
        case "product":
          return cmp(a.product_type ?? a.job_title ?? null, b.product_type ?? b.job_title ?? null);
        case "quantity":
          return cmp(a.quantity ?? null, b.quantity ?? null);
        case "price":
          return cmp(ra.price, rb.price);
        case "status":
          return cmp(a.status ?? null, b.status ?? null);
        case "placed":
          return cmp(new Date(a.created_at).getTime(), new Date(b.created_at).getTime());
      }
    });
    return rows;
  }, [filtered, cuttingTiers, ordersSortKey, ordersSortDir]);

  const SortHeader = ({
    k,
    label,
    align,
  }: {
    k: OrdersSortKey;
    label: string;
    align?: "left" | "right";
  }) => {
    const active = ordersSortKey === k;
    const Icon = !active ? ArrowUpDown : ordersSortDir === "asc" ? ArrowUp : ArrowDown;
    return (
      <th className="px-5 py-4">
        <button
          type="button"
          onClick={() => toggleOrdersSort(k)}
          className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground ${
            active ? "text-foreground" : ""
          } ${align === "right" ? "flex-row-reverse" : ""}`}
          aria-sort={active ? (ordersSortDir === "asc" ? "ascending" : "descending") : "none"}
        >
          {label}
          <Icon className="h-3 w-3" />
        </button>
      </th>
    );
  };

  return (
    <div className="space-y-5">
      <StaffTodaySummary />
      <div>
        <h1 className="text-3xl font-bold">Jobs table</h1>
        <p className="text-muted-foreground">Quick scan, filter and search.</p>
      </div>

      {!prefsReady ? (
        <>
          <div className="flex flex-wrap gap-3">
            <Skeleton className="h-8 w-48 rounded-full" />
            <Skeleton className="h-9 w-72 rounded-full" />
            <Skeleton className="h-9 w-56 rounded-full" />
            <Skeleton className="h-9 w-48 rounded-full" />
            <Skeleton className="h-9 w-32 rounded-full" />
          </div>
          <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <div className="space-y-3 p-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center rounded-full border border-border bg-background p-0.5 gap-0.5">
              <button
                onClick={() => nav({ to: "/app/staff/jobs-board" })}
                className="rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Board
              </button>
              <button
                onClick={() => setView("table")}
                className={
                  view === "table"
                    ? "rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
                    : "rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                }
                aria-current={view === "table" ? "page" : undefined}
              >
                Jobs
              </button>
              <button
                onClick={() => setView("orders")}
                className={
                  view === "orders"
                    ? "rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
                    : "rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                }
                aria-current={view === "orders" ? "page" : undefined}
              >
                Orders
              </button>
            </div>
            <Input
              placeholder="Search jobs or customers…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-72 rounded-full"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56 rounded-full">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {JOB_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-48 rounded-full">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                <SelectItem value="resolute">Resolute</SelectItem>
                <SelectItem value="craftyjet">CraftyJet</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={myJobsOnly}
                onChange={(e) => setMyJobsOnly(e.target.checked)}
                className="rounded"
              />
              My jobs only
            </label>
            <label
              className={
                "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors " +
                (showArchived
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  : "border-border bg-background")
              }
            >
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded"
              />
              Show archived
            </label>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            {isFetching && !isLoading && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-background/40 backdrop-blur-[1px]">
                <div className="mt-4 flex items-center gap-2 rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-soft">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Refreshing…
                </div>
              </div>
            )}
            {isLoading ? (
              <div className="px-5 py-12 text-center text-muted-foreground">Loading jobs…</div>
            ) : view === "orders" ? (
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <SortHeader k="order_number" label="Order #" />
                    <SortHeader k="product" label="Product" />
                    <th className="px-5 py-4">Customer</th>
                    <SortHeader k="quantity" label="Qty" />
                    <SortHeader k="price" label="Price" />
                    <SortHeader k="status" label="Status" />
                    <SortHeader k="placed" label="Placed" />
                  </tr>
                </thead>
                <tbody>
                  {orderRows.map(({ job: j, price }) => (
                    <tr key={j.id} className="border-t border-border hover:bg-secondary/30">
                      <td className="px-5 py-4 font-mono">
                        <Link
                          to="/app/staff/jobs/$id"
                          params={{ id: j.id }}
                          className="font-semibold hover:text-primary"
                        >
                          {(j as { order_number?: number | null }).order_number != null
                            ? `#${String((j as { order_number?: number | null }).order_number).padStart(6, "0")}`
                            : `#${j.id.slice(0, 8).toUpperCase()}`}
                        </Link>
                      </td>
                      <td className="px-5 py-4">
                        <div>{j.product_type ?? j.job_title}</div>
                        {j.print_size && (
                          <div className="text-xs text-muted-foreground">{j.print_size}</div>
                        )}
                      </td>
                      <td className="px-5 py-4">{j.customer?.company_name ?? "—"}</td>
                      <td className="px-5 py-4 tabular-nums">{j.quantity ?? "—"}</td>
                      <td className="px-5 py-4 tabular-nums">
                        {price !== null ? fmt(price) : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <StatusChip status={j.status} />
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {format(new Date(j.created_at), "d MMM HH:mm")}
                      </td>
                    </tr>
                  ))}
                  {orderRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">
                        No orders match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-4">Job</th>
                    <th className="px-5 py-4">Customer</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Brand</th>
                    <th className="px-5 py-4">Product</th>
                    <th className="px-5 py-4">Cutting</th>
                    <th className="px-5 py-4">Assignee</th>
                    <th className="px-5 py-4">Qty</th>
                    <th className="px-5 py-4">Due</th>
                    <th className="px-5 py-4">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((j) => {
                    const cuttingTier = cuttingTiers.find((t) => t.id === j.cutting_tier_id);
                    const assignee = staffProfiles.find(
                      (p: { id: string }) => p.id === j.assignee_profile_id,
                    );
                    return (
                      <tr key={j.id} className="border-t border-border hover:bg-secondary/30">
                        <td className="px-5 py-4">
                          <Link
                            to="/app/staff/jobs/$id"
                            params={{ id: j.id }}
                            className="font-semibold hover:text-primary"
                          >
                            {j.job_title}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {j.print_size} · {j.colours}
                          </div>
                        </td>
                        <td className="px-5 py-4">{j.customer?.company_name ?? "—"}</td>
                        <td className="px-5 py-4">
                          <Select
                            value={j.status}
                            onValueChange={(value) => handleStatusChange(j.id, value as JobStatus)}
                          >
                            <SelectTrigger className="h-8 w-32 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {JOB_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4">
                          {j.customer?.brand
                            ? j.customer.brand.charAt(0).toUpperCase() + j.customer.brand.slice(1)
                            : "—"}
                        </td>
                        <td className="px-5 py-4">{j.product_type ?? "—"}</td>
                        <td className="px-5 py-4">{cuttingTier ? cuttingTier.name : "—"}</td>
                        <td className="px-5 py-4">
                          {assignee ? (assignee.full_name ?? assignee.email) : "—"}
                        </td>
                        <td className="px-5 py-4">{j.quantity}</td>
                        <td className="px-5 py-4">
                          {j.due_date ? format(new Date(j.due_date), "d MMM") : "—"}
                        </td>
                        <td className="px-5 py-4 text-muted-foreground">
                          {format(new Date(j.updated_at), "d MMM HH:mm")}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-5 py-12 text-center text-muted-foreground">
                        No jobs match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
